import React, {
    lazy,
    Suspense,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Layout } from 'antd';
import GrowthAnalysis from 'components/pages/GrowthAnalysis';
import Navbar from 'components/organism/Navbar';
import Filter from 'components/organism/Filter';
import PromoScorecard from 'components/pages/PromoScorecard';
import BumpReport from 'components/pages/BumpReport';
import PromoLandscape from 'components/pages/PromoLandscape';
import Login from 'components/pages/Login';
import PortfolioAnalysis from 'components/pages/PortfolioAnalysis';
import GlobalProvider from 'contexts';
import PromoRecommendation from 'components/pages/PromoRecommendation';
import { PromoRecommendationContext } from 'contexts/PromoRecommendationContext';
import PriceLadder from 'components/pages/PriceLadder';
import OpportunityIdentification from 'components/pages/OportunityIdentification';
import PriceCurve from 'components/pages/PriceCurve';
import PriceChange from 'components/pages/PriceChange';
import PricePoint from 'components/pages/PricePoint';
import PriceTracking from 'components/pages/PriceTracking';
import { PriceSimulatorContext } from 'contexts/PriceSimulatorContext';
import { PricingSimulatorContext } from 'contexts/PricingSimulatorContext';
import PriceSimulator from 'components/pages/PriceSimulator';
import { ConfigContext, UserContext } from 'contexts/ConfigContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import { LOGOUT_USER } from 'utils/mixpanelEventNames';
import MainLogo from 'assets/images/logo.png';
import PVMAnalysis from 'components/pages/PVMAnalysis';
import Depletion from 'components/pages/Depletion';
import axios from 'axios';
import { fetchTokenFromLocalStorage, getConfig, handleTokenErrorAndRedirect } from 'utils/helperMethods';
import CommercialForecast from 'components/pages/CommercialForecast';
import { PricingSimulator } from 'components/pages';
import { UpdateOrViewPricingSimulator } from 'components/organism/PricingSimulator';
import UpdateOrViewPricingAnalytics from 'components/organism/PricingSimulator/UpdateOrViewPricingAnalytics';
import { PricingAnalyticsContext } from 'contexts/PricingAnalyticsContext';
import { CreatePriceSimulator } from 'components/organism';
import { TPOContext } from 'contexts/TPOContext';
import TPOPage from 'components/pages/TPOPage';
import UpdateOrViewTPO from 'components/organism/TradePromoOptimization/UpdateOrViewTPO';
import { PricingSimulatorWrapperContext } from 'contexts/PricingSimulatorWrapperContext';
import AppVersionRefresh from 'components/AppVersionRefresh';
import ProductFruitHelpdesk from 'components/molecules/ProductFruitHelpdesk';
import JiraComponent from 'components/organism/JiraComponent';
import Loader from 'components/molecules/Loader';
import GlobalFilterRibbon from 'components/molecules/Filter/GlobalFilterRibbon';

import BestWeeksSection from 'components/template/PromoRecommendation/BestWeeksSection';
import auth0 from 'auth0-js';
import useTokenRenewal from 'hooks/general/useTokenRenewal';
import { ThemeProvider } from '@mui/material';
import { Provider } from 'react-redux';
import store from 'globals/redux/store/store';
import theme from 'globals/themes/theme';
import PPA from 'components/template/PPA';
import Home from 'components/pages/Home';
import { Header } from 'components/molecules/common/Header';
import { Footer } from 'components/molecules/common/Footer';
import WhiteSpaceAnalysis from 'components/template/WhiteSpaceAnalysis';
import P4POverview from 'components/template/P4POverview';
import P4PPerformance from 'components/template/P4PPerformance';
import P4PSimulator from 'components/template/P4PSimulator';
import DemandTransference from 'components/template/DemandTransference';
import PackArchitecture from 'components/template/PackArchitecture';
import PVMAnalysisIframe from 'components/template/PVMAnalysis';
import AnnualOperatingPlan from 'components/template/AnnualOperatingPlan';
import JumpBackInViewAll from 'components/template/JumpBackInViewAll';
import AssortmentScorecard from 'components/template/AssortmentScorecard';
import ProfitPoolOverview from 'components/template/ProfitPoolOverview';
import Alert from 'components/template/Alert';
import LTReport from 'components/template/LTReport';
import Planner from 'components/template/Planner';
import TradeOverview from 'components/template/Trade Overview';
import TradePerformance from 'components/template/TradePerformance';
import TradeTermsSimulator from 'components/template/TradeTermsSimulator';
import CommercialPolicySimulator from 'components/template/CommercialPolicySimulator';
import HomeNew from 'components/pages/HomeNew';
import ScenarioDecomposition from 'components/pages/ScenarioDecomposition';
import PLComparison from 'components/template/PLComparison';
import ExecutiveSummary from 'components/template/ExecutiveSummary';
import PriceThreshold from 'components/template/PriceThreshold';
import PriceChangeDecomposition from 'components/template/PriceChangeDecomposition';
import DriversOfProfitGrowth from 'components/template/DriversOfProfitGrowth';
import AssortmentPerformanceRanking from 'components/template/AssortmentPerformanceRanking';
import AssortmentPlanning from 'components/template/AssortmentPlanning';
import BaseVolumePlanning from 'components/template/BaseVolumePlanning';
import { ConfigProvider as AntdV5ConfigProvider, notification as antdV5Notification } from 'antd-v5';
import { AskAsper as AskAsper3 } from 'components/pages/AskAsper3/AskAsper/AskAsper';
import { getAskAsperPortalRoot } from 'components/pages/AskAsper3/AskAsper/utils/askAsperScope';
import {
    endAskAsperSession,
    ASK_ASPER_SESSION_END_REASON,
} from 'components/pages/AskAsper3/AskAsper/utils/askAsperSessionTracking';
import ProfitPool from 'components/pages/ProfitPoolOverview/ProfitPool';
import ProfitPoolEvolution from 'components/pages/PPEvolution/ProfitPoolEvolution';
import GrowthMarginDrivers from 'components/pages/GrowthMarginDrivers/GrowthMarginDrivers';
import StrategicOpportunity from 'components/pages/StrategicOpportunity/StrategicOpportunity';
import GrowthMarginBridges from 'components/pages/GrowthMarginBridges/GrowthMarginBridges';
import { ProfitPoolOverviewContextProvider } from 'components/pages/ProfitPoolOverview/context/ProfitPoolOverviewContext';
import ShipmentGrowthAnalysis from 'components/pages/ShipmentGrowthAnalysis';
import ShipmentBumpReport from 'components/pages/ShipmentBumpReport';
import { ProfitPoolEvolutionContextProvider } from 'components/pages/PPEvolution/context/ProfitPoolEvolutionContext';
import { GrowthMarginDriversContextProvider } from 'components/pages/GrowthMarginDrivers/context/GrowthMarginDriversContext';
import { GrowthMarginBridgesProvider } from 'components/pages/GrowthMarginBridges/GrowthMarginBridgesContext';
import { isAdminUser, isSuperAdmin, canAccessAdminFeature, ADMIN_ROUTE_FEATURE_MAP } from 'constants/admin';
import { FilterContextData } from 'contexts/FilterContext';

import PPADashboard from 'components/pages/PPAHomePage';
import PpaScopeHydrator from 'components/pages/PpaScopeHydrator';
import PerformanceSummaryPage from 'components/pages/PerformanceSummary/PerformanceSummary';
import PriceSimulatorV3 from 'components/pages/PriceSimulatorV3';
import PriceSimulatorV3ProjectScenarios from 'components/pages/PriceSimulatorV3ProjectScenarios';
import WhitespaceAnalysis from 'components/pages/WhitespaceAnalysis';
import { PriceSimulatorV3ContextWrapper } from 'contexts/PriceSimulatorV3ContextWrapper';
import { WhitespaceAnalysisContextWrapper } from 'contexts/WhitespaceAnalysisContext';
//DEFINE ALL APPLICATION ROUTES HERE
//WHENEVER A NEW ROUTE IS ADDED, ADD IT IN CONFIG ALSO (config keys:screens,screenDetailsWithRoutes )

const AdminSettings = lazy(() => import('components/pages/AdminSettings'));
const CacheManagement = lazy(() => import('components/pages/CacheManagement'));
const ForceRefresh = lazy(() => import('components/pages/ForceRefresh'));
const SimulatorValidation = lazy(() => import('components/pages/SimulatorValidation'));
const PPASimulatorAdmin = lazy(() => import('components/pages/PPASimulatorAdmin'));
const AppLightup = lazy(() => import('components/pages/AppLightup'));
const MigrationHistory = lazy(() => import('components/pages/MigrationHistory'));
const DERefreshJobs = lazy(() => import('components/pages/DERefreshJobs'));
const EvalManagementPage = lazy(() =>
    import('components/pages/AskAsper3/AskAsper/eval/EvalManagementPage').then((m) => ({
        default: m.EvalManagementPage,
    })),
);

//pageId-component mapping
const routeComponentMapping: any = {
    // home: <Home />,
    home: <HomeNew />,
    pvm: <PVMAnalysis />,
    ga: <GrowthAnalysis from="growth-analysis" />,
    sga: <ShipmentGrowthAnalysis />,
    br: <BumpReport from="bump-report" />,
    sbr: <ShipmentBumpReport />,
    da: <Depletion />,
    pcn: <PriceChange />,
    pth: <PriceThreshold />,
    asp: <AssortmentPlanning />,
    bvp: <BaseVolumePlanning />,
    pcd: <PriceChangeDecomposition />,
    dpg: <DriversOfProfitGrowth />,
    assr: <AssortmentPerformanceRanking />,
    pp: <PricePoint />,
    pld: <PriceLadder />,
    pc: <PriceCurve />,
    pt: <PriceTracking />,
    oi: <OpportunityIdentification />,
    ps: <PromoScorecard />,
    pl: <PromoLandscape />,
    pa: <PortfolioAnalysis />,
    bw: <BestWeeksSection />,
    alert: <Alert />,
    ltr: <LTReport />,
    plr: <Planner />,
    to: <TradeOverview />,
    tp: <TradePerformance />,
    tts: <TradeTermsSimulator />,
    cps: <CommercialPolicySimulator />,
    pr: (
        <PromoRecommendationContext>
            <PromoRecommendation />
        </PromoRecommendationContext>
    ),
    psr: <PriceSimulator />,
    psrNew: (
        <PriceSimulatorContext>
            <CreatePriceSimulator />
        </PriceSimulatorContext>
    ),
    psrView: (
        <PriceSimulatorContext>
            <CreatePriceSimulator />
        </PriceSimulatorContext>
    ),
    psrEdit: (
        <PriceSimulatorContext>
            <CreatePriceSimulator />
        </PriceSimulatorContext>
    ),
    cf: <CommercialForecast />,
    // v2
    psrV2: (
        <PricingSimulatorWrapperContext>
            <PricingSimulatorContext>
                <PricingSimulator />
            </PricingSimulatorContext>
        </PricingSimulatorWrapperContext>
    ),
    // v2
    psrV2View: (
        <PricingSimulatorWrapperContext>
            <PricingSimulatorContext>
                <UpdateOrViewPricingSimulator />
            </PricingSimulatorContext>
        </PricingSimulatorWrapperContext>
    ),
    // v2
    psrV2Update: (
        <PricingSimulatorWrapperContext>
            <PricingSimulatorContext>
                <UpdateOrViewPricingSimulator />
            </PricingSimulatorContext>
        </PricingSimulatorWrapperContext>
    ),
    // v2
    psa: (
        <PricingSimulatorWrapperContext>
            <PricingSimulatorContext>
                <PricingSimulator />
            </PricingSimulatorContext>
        </PricingSimulatorWrapperContext>
    ),
    // v2
    psaUpdate: (
        <PricingSimulatorWrapperContext>
            <PricingAnalyticsContext>
                <UpdateOrViewPricingAnalytics />
            </PricingAnalyticsContext>
        </PricingSimulatorWrapperContext>
    ),
    // v2
    psaView: (
        <PricingSimulatorWrapperContext>
            <PricingAnalyticsContext>
                <UpdateOrViewPricingAnalytics />
            </PricingAnalyticsContext>
        </PricingSimulatorWrapperContext>
    ),
    tpo: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    plan: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    viewUpdatePlan: (
        <TPOContext>
            <UpdateOrViewTPO />
        </TPOContext>
    ),
    bestWeeksTool: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    playbookTool: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    promoLibraryTool: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    eventLibraryTool: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    report: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    sd: <ScenarioDecomposition />,
    wsa: <WhiteSpaceAnalysis />,
    es: <ExecutiveSummary />,
    p4po: <P4POverview />,
    p4pp: <P4PPerformance />,
    p4ps: <P4PSimulator />,
    plc: <PLComparison />,
    ppo: (
        <ProfitPoolOverviewContextProvider>
            <ProfitPool />
        </ProfitPoolOverviewContextProvider>
    ),
    ppevo: (
        <ProfitPoolEvolutionContextProvider>
            <ProfitPoolEvolution />
        </ProfitPoolEvolutionContextProvider>
    ),
    gmd: (
        <GrowthMarginDriversContextProvider>
            <GrowthMarginDrivers />
        </GrowthMarginDriversContextProvider>
    ),
    gmb: (
        <GrowthMarginBridgesProvider>
            <GrowthMarginBridges />
        </GrowthMarginBridgesProvider>
    ),
    adminIndex: <AdminSettings />,
    so: <StrategicOpportunity />,
    cm: <CacheManagement />,
    fr: <ForceRefresh />,
    sv: <SimulatorValidation />,
    ppaSim: <PPASimulatorAdmin />,
    al: <AppLightup />,
    mh: <MigrationHistory />,
    drj: <DERefreshJobs />,
    ppaHome: <PPADashboard />,
    ppaPerfSummary: <PerformanceSummaryPage />,
    wa: (
        <WhitespaceAnalysisContextWrapper>
            <WhitespaceAnalysis />
        </WhitespaceAnalysisContextWrapper>
    ),
    psrV3: (
        <PriceSimulatorV3ContextWrapper>
            <PriceSimulatorV3 />
        </PriceSimulatorV3ContextWrapper>
    ),
    psrV3ProjectScenarios: (
        <PriceSimulatorV3ContextWrapper>
            <PriceSimulatorV3ProjectScenarios />
        </PriceSimulatorV3ContextWrapper>
    ),
};

//AUTH0 WEB AUTH
const webAuth = new auth0.WebAuth({
    domain: process.env.REACT_APP_AUTH0_DOMAIN_URL ?? '',
    clientID: process.env.REACT_APP_AUTH0_CLIENT_ID ?? '',
    audience: process.env.REACT_APP_AUTH0_AUDIENCE ?? '',
    scope: 'openid profile email',
    responseType: 'token id_token',
    redirectUri: `${window.location.origin}/callback`,
});

export default function App() {
    const navigate = useNavigate();
    const route = useLocation();
    // //fetching new token when old token expires.
    // oktaAuth.tokenManager.on('expired', function (key, expiredToken) {
    //     oktaAuth.tokenManager.renew(key);
    // });
    const onLogout = useCallback(() => {
        trackEventMixpanel(LOGOUT_USER);
        // End an active Ask Asper session with the logout reason before storage is cleared,
        // so Mixpanel can still read the stored email/token. No-op if no session is active.
        endAskAsperSession(ASK_ASPER_SESSION_END_REASON.LOGOUT);
        webAuth.logout({ returnTo: window.location.origin });
        localStorage.clear();
    }, [webAuth]);

    //token renewal auth0
    useTokenRenewal({ webAuth });

    //to control visibility of JIRA support button and freshdesk widget
    useEffect(() => {
        const button = document.getElementById('jsd-widget') as HTMLElement;
        const freshdeskButton = document.getElementById('freshworks-container') as HTMLElement;
        //for freshdesk
        if (button && process.env.REACT_APP_JIRA_HELP !== 'true') {
            button.style.display = 'none';
            return;
        }

        //for jira
        if (button && route.pathname.length < 2) {
            button.style.visibility = 'hidden';
        } else if (button && process.env.REACT_APP_JIRA_HELP === 'true') {
            button.style.visibility = 'visible';
            //    button.style.display = 'block';
            button.style.bottom = '-8px';
            button.style.userSelect = 'none';
        }

        //for freshdesk
        if (freshdeskButton && process.env.REACT_APP_FRESHDESK_WIDGET_ID === 'null') {
            freshdeskButton.style.display = 'none';
            return;
        }
        if (route.pathname.length < 2 && freshdeskButton) {
            freshdeskButton.style.display = 'none';
        } else if (freshdeskButton && process.env.REACT_APP_FRESHDESK_WIDGET_ID !== 'null') {
            freshdeskButton.style.display = 'block';
            freshdeskButton.style.userSelect = 'none';
        }
    }, [route]);

    //to control visibility of JIRA support button and freshdesk widget
    useEffect(() => {
        const button = document.getElementById('jsd-widget') as HTMLElement;
        const freshdeskButton = document.getElementById('freshworks-container') as HTMLElement;
        //for freshdesk
        if (button && process.env.REACT_APP_JIRA_HELP !== 'true') {
            button.style.display = 'none';
            return;
        }

        //for jira
        if (button && route.pathname.length < 2) {
            button.style.visibility = 'hidden';
        } else if (button && process.env.REACT_APP_JIRA_HELP === 'true') {
            button.style.visibility = 'visible';
            //    button.style.display = 'block';
            button.style.bottom = '-8px';
            button.style.userSelect = 'none';
        }

        //for freshdesk
        if (freshdeskButton && process.env.REACT_APP_FRESHDESK_WIDGET_ID === 'null') {
            freshdeskButton.style.display = 'none';
            return;
        }
        if (route.pathname.length < 2 && freshdeskButton) {
            freshdeskButton.style.display = 'none';
        } else if (freshdeskButton && process.env.REACT_APP_FRESHDESK_WIDGET_ID !== 'null') {
            freshdeskButton.style.display = 'block';
            freshdeskButton.style.userSelect = 'none';
        }
    }, [route]);

    //this is to handle when user directly uses url/refreshes
    useEffect(() => {
        if (!route.pathname.includes('callback') && route.pathname !== '/') {
            navigate('/', { state: { path: window.location.pathname } });
        }
    }, []);

    //fetch auth token and add in header
    useEffect(() => {
        axios.interceptors.request.use(function (config) {
            const token = fetchTokenFromLocalStorage();
            config.headers.Authorization = `Bearer ${token}`;
            return config;
        });

        //intercepting errors
        axios.interceptors.response.use(undefined, (error) => {
            // if (typeof error.response?.data === 'object') return Promise.reject(error);
            const parsedError = error?.response;

            //condition to check if token is invalid
            //refresh page if token is invalid so that new token will be generated
            //this will redirect user to login page if session is expired
            handleTokenErrorAndRedirect(parsedError);

            return Promise.reject(error);
        });
    }, []);

    return (
        <div>
            <div>
                <Routes>
                    <Route
                        path="*"
                        element={
                            <ConfigContext>
                                {/* Wraps MainContainer so FilterContext is available for collapsible global filter ribbon */}
                                <GlobalProvider>
                                    <MainContainer onLogout={onLogout} />
                                </GlobalProvider>
                            </ConfigContext>
                        }
                    />
                    <Route
                        path="/"
                        element={
                            <ConfigContext>
                                <LoginContainer onLogout={onLogout} />
                            </ConfigContext>
                        }
                    />
                    <Route
                        path="/callback"
                        element={
                            <ConfigContext>
                                <LoginContainer onLogout={onLogout} />
                            </ConfigContext>
                        }
                    />
                </Routes>
            </div>
        </div>
    );
}

type LoginProps = {
    onLogout: Function;
};
//LOGIN COMPONENT
const LoginContainer = ({ onLogout }: LoginProps) => (
    <div className="h-full w-full">
        <Routes>
            <Route path="*" element={<Login onLogout={onLogout} webAuth={webAuth} />} />
        </Routes>
    </div>
);

// Path prefix for admin-only routes; non-admin users are redirected to home when visiting these
const ADMIN_PATH_PREFIX = '/admin';

// Wraps AskAsper routes with the antd-v5 + MUI theme scope they require
const AskAsperRouteScope = ({ children }: { children: React.ReactNode }) => {
    // All AskAsper overlays (modals, dropdowns, popovers, notifications) mount into a
    // single body-level portal root carrying `.askasper-scope`, so scoped design-library
    // styles apply and nothing is clipped by the in-route shell's overflow:hidden.
    useEffect(() => {
        antdV5Notification.config({ getContainer: getAskAsperPortalRoot });
        return () => {
            antdV5Notification.config({ getContainer: () => document.body });
        };
    }, []);

    return (
        <AntdV5ConfigProvider
            prefixCls="ant5"
            iconPrefixCls="ant5icon"
            getPopupContainer={getAskAsperPortalRoot}
            getTargetContainer={getAskAsperPortalRoot}
        >
            <ThemeProvider theme={theme}>
                <div
                    className="askasper-scope"
                    style={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {children}
                </div>
            </ThemeProvider>
        </AntdV5ConfigProvider>
    );
};

// Path segments for routes that should hide the global filter ribbon.
// NOTE: 'ask-asper' is intentionally NOT listed here — it is gated by the
// `enableAskAsper` feature flag in the `removeGF` computation below.
const ROUTES_WITHOUT_GLOBAL_FILTER = [
    'pricing-simulator',
    'pricing-analytics',
    'tpo',
    'price-pack-architecture',
    'whitespace-analysis',
    'demand-transference',
    'price-pack-simulator',
    'pvm-analysis',
    'assortment-scorecard',
    'profit-pool-overview',
    'lt-report',
    'planner',
    'trade-overview',
    'trade-performance',
    'trade-terms-simulator',
    'p4p-overview',
    'p4p-performance',
    'p4p-simulator',
    'white-space-analysis',
    'executive-summary',
    'price-threshold',
    'price-change-decomposition',
    'drivers-of-profit-growth',
    'assortment-performance-ranking',
    'assortment-planning',
    'base-volume-planning',
    'commercial-policy-simulator',
    'ai-decision-hub',
    'all-alerts',
    'admin',
    'cache-management',
    'simulator-validation',
    'price-simulator-v3',
    'ppa/home',
];

type MainRoutesProps = {
    appConfig: any;
    enableAskAsper: boolean;
    isEvalEnabled: boolean;
};

// Routed content rendered inside MainContainer; extracted to keep MainContainer within size limits
const MainRoutes = ({ appConfig, enableAskAsper, isEvalEnabled }: MainRoutesProps) => (
    <Suspense
        fallback={
            <div className="w-full bg-white" style={{ minHeight: 'calc(100vh - 100px)' }}>
                <Loader size="default" />
            </div>
        }
    >
        <Routes>
            {appConfig?.screenDetailsWithRoutes?.flatMap((screen: any) => {
                const baseRoute = (
                    <Route key={screen.path} path={screen.path} element={routeComponentMapping[screen.pageId]} />
                );

                // Adding subRoutes if present
                const subRoutes = screen.subRoutes?.flatMap((subRoute: any) => (
                    <Route
                        key={`${screen.path}/${subRoute.suffix}`}
                        path={`${screen.path}/${subRoute.suffix}`}
                        element={routeComponentMapping[subRoute?.pageId]}
                    />
                ));

                return [baseRoute, ...(subRoutes || [])];
            })}
            {enableAskAsper && (
                <Route
                    path="/ask-asper"
                    element={
                        <AskAsperRouteScope>
                            <AskAsper3 isEvalEnabled={isEvalEnabled} />
                        </AskAsperRouteScope>
                    }
                />
            )}
            {isEvalEnabled && (
                <Route
                    path="/ask-asper/eval"
                    element={
                        <AskAsperRouteScope>
                            <EvalManagementPage />
                        </AskAsperRouteScope>
                    }
                />
            )}
            <Route path="/pricing/analytics/price-pack-simulator" element={<PPA />} />
            <Route path="/pricing/analytics/whitespace-analysis" element={<WhiteSpaceAnalysis />} />
            <Route path="/pricing/analytics/p4p-overview" element={<P4POverview />} />
            <Route path="/pricing/analytics/p4p-performance" element={<P4PPerformance />} />
            <Route path="/pricing/analytics/p4p-simulator" element={<P4PSimulator />} />
            <Route path="/pricing/analytics/demand-transference" element={<DemandTransference />} />
            <Route path="/pricing/analytics/price-pack-architecture" element={<PackArchitecture />} />
            <Route path="/promo/category-diagnostics/pvm-analysis" element={<PVMAnalysisIframe />} />
            <Route path="/ai-decision-hub/annual-operating-plan" element={<AnnualOperatingPlan />} />
            <Route path="/all-alerts/view-all" element={<JumpBackInViewAll />} />
            <Route path="/assortment/assortment-scorecard" element={<AssortmentScorecard />} />
            <Route path="/alert" element={<Alert />} />
            <Route path="/lt-report" element={<LTReport />} />
            <Route path="/planner" element={<Planner />} />
            <Route path="/promo/category-diagnostics/trade-overview" element={<TradeOverview />} />
            <Route path="/trade-architecture/profit-pool-overview" element={<ProfitPoolOverview />} />
            <Route path="/promo/category-diagnostics/trade-performance" element={<TradePerformance />} />
            <Route path="/promo/category-diagnostics/trade-terms-simulator" element={<TradeTermsSimulator />} />
            <Route path="/promo/analytics/commercial-policy-simulator" element={<CommercialPolicySimulator />} />
            <Route path="*" element={<Navigate to={appConfig?.screenDetailsWithRoutes?.[0]?.path} />} />
        </Routes>
    </Suspense>
);

//WHENEVER A NEW ROUTE IS ADDED, ADD IT IN CONFIG ALSO (config keys:screens,screenDetailsWithRoutes )
//ALL COMPONENTS EXCEPT LOGIN
// @code-guard: skip-next-function reason="App shell orchestrator: routing + global filter + ribbon layout; refactor planned"
const MainContainer = ({ onLogout }: any) => {
    const { appConfig } = useContext(UserContext);
    const location = useLocation();
    const navigate = useNavigate();
    const { isGlobalFilterCollapsed, expandFilterBol } = useContext(FilterContextData);
    // Measure fixed ribbon height so content below is not covered (ribbon is position: fixed)
    const globalFilterRibbonRef = useRef<HTMLDivElement | null>(null);
    const [globalFilterRibbonSpacerPx, setGlobalFilterRibbonSpacerPx] = useState(44);

    const commonConfig = getConfig('commonConfig', appConfig);
    const isGlobalFilterCollapsable = commonConfig?.isGlobalFilterCollapsable || false;
    const enableAskAsper = commonConfig?.enableAskAsper === true;
    const isEvalEnabled = enableAskAsper && commonConfig?.isEvalEnabled === true;

    // Redirect non-admin users away from admin routes (e.g. direct URL / refresh)
    useEffect(() => {
        if (!location.pathname.startsWith(ADMIN_PATH_PREFIX)) return;

        if (!isAdminUser()) {
            const homePath = appConfig?.screenDetailsWithRoutes?.[0]?.path;
            navigate(homePath ? `/${homePath}` : '/', { replace: true });
            return;
        }

        if (!isSuperAdmin()) {
            const featureKey = ADMIN_ROUTE_FEATURE_MAP[location.pathname];
            if (featureKey && !canAccessAdminFeature(featureKey)) {
                navigate('/admin', { replace: true });
            }
        }
    }, [location.pathname, appConfig?.screenDetailsWithRoutes, navigate]);

    // Redirect when AskAsper is disabled but user navigates to /ask-asper directly
    useEffect(() => {
        if (!enableAskAsper && location.pathname.includes('ask-asper')) {
            const homePath = appConfig?.screenDetailsWithRoutes?.[0]?.path;
            navigate(homePath ? `/${homePath}` : '/', { replace: true });
        }
    }, [enableAskAsper, location.pathname, appConfig?.screenDetailsWithRoutes, navigate]);

    const showJiraBugReport: boolean = process.env.REACT_APP_JIRA_ENABLE_BUG_REPORT === 'true' ? true : false;

    const removeGF =
        ROUTES_WITHOUT_GLOBAL_FILTER.some((segment) => location.pathname?.includes(segment)) ||
        (enableAskAsper && location.pathname?.includes('ask-asper'));

    const isHomePage = location.pathname === '/home';
    const isAskAsper = enableAskAsper && location.pathname?.includes('ask-asper');

    // Pin ribbon under navbar; left offset follows open sidebar width or full width when filter is collapsed
    const globalFilterRibbonFixedInset = useMemo(() => {
        const right = '1rem';
        const top = 'var(--navbar-height)';
        const zIndex = expandFilterBol ? 53 : 40;
        const transition = '500ms';
        if (removeGF && !isGlobalFilterCollapsable) {
            return { top, right, left: '1rem', zIndex, transition } as const;
        }
        if (isGlobalFilterCollapsed) {
            return {
                top,
                right,
                left: '0',
                zIndex,
                transition,
            } as const;
        }
        return {
            top,
            right,
            left: 'var(--global-filter-width)',
            zIndex,
            transition,
        } as const;
    }, [expandFilterBol, removeGF, isGlobalFilterCollapsed, isGlobalFilterCollapsable]);

    // Keep spacer height in sync when ribbon content or collapse state changes size
    useLayoutEffect(() => {
        const el = globalFilterRibbonRef.current;
        if (!el) {
            return undefined;
        }
        const sync = () => setGlobalFilterRibbonSpacerPx(el.getBoundingClientRect().height);
        sync();
        const ro = new ResizeObserver(sync);
        ro.observe(el);
        return () => ro.disconnect();
    }, [removeGF, isGlobalFilterCollapsed]);

    if (isHomePage)
        return (
            <div className="min-h-screen flex flex-col" style={{ background: 'white' }}>
                <Header logOut={onLogout} />
                {/* <Home /> */}
                <HomeNew />
                <Footer />
            </div>
        );
    return (
        <>
            <PpaScopeHydrator />
            {/* App version refresh Component*/}
            <AppVersionRefresh />
            <ProductFruitHelpdesk />
            <Layout className={isAskAsper ? 'h-screen font-primary' : 'h-full font-primary'}>
                <Navbar logOut={onLogout} />
                <div style={isAskAsper ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}}>
                    {!removeGF && <Filter />}
                    {!isAskAsper && (
                        <div className="fixed bottom-4 left-4 z-[10000] flex items-center gap-4">
                            <div className="global-filter-width flex items-center gap-3 justify-center">
                                <span className="text-fontGray font-medium font-xxs mb-[6px]"> Powered by </span>
                                <img style={{ height: 32 }} src={MainLogo} />
                                {/* <img style={{ width: '171px' }} src={PoweredByAsper} /> */}
                            </div>
                        </div>
                    )}
                    {showJiraBugReport && (
                        <div className="fixed bottom-4 right-24 z-[10000]">
                            <JiraComponent />
                        </div>
                    )}
                    <div
                        id="main-container"
                        style={
                            isAskAsper
                                ? {
                                      height: 'calc(100vh - var(--navbar-height))',
                                      marginTop: 'var(--navbar-height)',
                                      overflow: 'hidden',
                                      display: 'flex',
                                      flexDirection: 'column',
                                  }
                                : { minHeight: 'calc(100vh - 80px)', transition: '500ms ease' }
                        }
                        className={
                            isAskAsper
                                ? ''
                                : removeGF
                                ? 'm-4 main-screen-margin-top'
                                : `mt-4 mr-4 mb-4 global-filter-margin-left ${
                                      isGlobalFilterCollapsable
                                          ? 'main-screen-margin-top-no-margin-top'
                                          : 'main-screen-margin-top'
                                  }`
                        }
                    >
                        {isGlobalFilterCollapsable && !removeGF ? (
                            <>
                                <div
                                    ref={globalFilterRibbonRef}
                                    className="fixed w-full"
                                    style={globalFilterRibbonFixedInset}
                                >
                                    <GlobalFilterRibbon />
                                </div>
                                {/* Reserves vertical space for the fixed ribbon so routed content starts below it */}
                                <div className="shrink-0" style={{ height: globalFilterRibbonSpacerPx }} aria-hidden />
                            </>
                        ) : null}
                        <MainRoutes
                            appConfig={appConfig}
                            enableAskAsper={enableAskAsper}
                            isEvalEnabled={isEvalEnabled}
                        />
                    </div>
                </div>
            </Layout>
        </>
    );
};
